"use client"

import { PaginationType } from "@/app/types/pagination"
import { Avatar, Box, Button, Divider, NoSsr, Skeleton, Typography, colors } from "@mui/material"
import MediaV2 from "@v2/components/MediaV2"
import FlexBox from "@v2/components/styled/FlexBox"
import api from "@v2/lib/api"
import axios, { PaginatedAxiosResponse } from "@v2/lib/axios"
import dayjs from "@v2/lib/dayjs"
import { ChevronDown, HatGlassesIcon, User } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import VideoAddCommentButton from "./VideoAddCommentButton"

type VideoCommentsProps = {
    videoId: number
}

type VideoCommentType = {
    id: number
    created_at: string
    is_unknown: boolean
    content: string
    user: null | {
        first_name?: string
        last_name?: string
        title_name?: string
        avatar: null | {
            id: string
        }
    }
}

export default function VideoComments({ videoId }: VideoCommentsProps) {
    const [loading, setLoading] = useState(true)
    const [comments, setComments] = useState<VideoCommentType[]>([])
    const [pagination, setPagination] = useState<PaginationType>()

    const getComments = useCallback((page = 1) => {
        if (page === 1) setLoading(true)

        axios.get(api.videoComments(videoId) + `?limit=4&page=${page}`).then((res: PaginatedAxiosResponse<VideoCommentType[]>) => {
            setLoading(false)
            setComments(prev => page === 1 ? res.data.data : [...prev, ...res.data.data])
            setPagination(res.data.pagination)
        }).catch(() => {
            setLoading(false)
        })
    }, [videoId])

    useEffect(() => {
        getComments()
    }, [getComments])

    const loadMoreComments = useCallback(() => {
        if (!pagination?.current_page) return
        getComments(pagination.current_page + 1)
    }, [getComments, pagination?.current_page])

    const calcFromNow = useMemo(() => (datetime: string) => {
        return dayjs(datetime, "YYYY-MM-DD HH:mm:ss").locale("fa").fromNow()
    }, [])

    return (
        <NoSsr fallback={<VideoCommentsLoadingSkeleton />}>
            <Box sx={{ bgcolor: "#fff", p: 2 }}>
                <FlexBox sx={{ justifyContent: "space-between", mb: 2, minHeight: 48 }}>
                    <Typography id="comments" sx={{ fontSize: 18, fontWeight: 500, color: colors.grey[800] }}>
                        دیدگاه کاربران
                        {(!!pagination?.total && pagination.total > 0) && (
                            <Typography component="span" sx={{ ml: 1, fontSize: 13 }}>
                                ({pagination.total})
                            </Typography>
                        )}
                    </Typography>

                    <VideoAddCommentButton videoId={videoId} onSubmitted={() => getComments(1)} />
                </FlexBox>

                {loading ? (
                    <LoadingSkeleton />
                ) : comments.length > 0 ? (
                    <Box>
                        {comments.map((comment, index) => {
                            const commentText = comment.content ?? ""
                            const userName = comment.is_unknown
                                ? "کاربر ناشناس"
                                : (comment.user?.first_name || comment.user?.title_name || "کاربر")

                            return (
                                <Box key={comment.id}>
                                    <FlexBox sx={{ alignItems: "flex-start", gap: 1 }}>
                                        <Avatar sx={{ bgcolor: colors.grey[200], outline: "4px solid #fff" }}>
                                            {comment.is_unknown ? (
                                                <HatGlassesIcon />
                                            ) : comment.user?.avatar ? (
                                                <MediaV2 media_id={comment.user.avatar?.id} alt="user_profile" width={40} height={40} />
                                            ) : (
                                                <User />
                                            )}
                                        </Avatar>

                                        <Box sx={{ flex: 1 }}>
                                            <FlexBox sx={{ mt: 1 }}>
                                                <Typography component="span" sx={{ fontSize: "16px !important", fontWeight: 500, color: colors.grey[900] }}>
                                                    {userName}
                                                </Typography>
                                                <Divider flexItem orientation="vertical" sx={{ mx: 1, my: 0.5 }} />
                                                <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: colors.grey[600] }}>
                                                    {calcFromNow(comment.created_at)}
                                                </Typography>
                                            </FlexBox>

                                            <Typography sx={{
                                                fontSize: 15,
                                                color: colors.grey[800],
                                                textAlign: "justify",
                                                mt: 2,
                                                whiteSpace: "pre-line",
                                                lineHeight: "27px"
                                            }}>
                                                {commentText}
                                            </Typography>
                                        </Box>
                                    </FlexBox>
                                    {comments.length - 1 !== index && (
                                        <Divider flexItem sx={{ my: 2 }} />
                                    )}
                                </Box>
                            )
                        })}

                        {pagination?.total ? ((pagination.total > comments.length) && (
                            <FlexBox sx={{ justifyContent: "center", mt: 2 }}>
                                <Button
                                    color="inherit"
                                    onClick={loadMoreComments}
                                    sx={{ height: 48, minWidth: 200 }}
                                    variant="outlined"
                                    endIcon={<ChevronDown />}
                                >
                                    نمایش دیدگاه های بیشتر
                                </Button>
                            </FlexBox>
                        )) : null}
                    </Box>
                ) : (
                    <FlexBox sx={{ textAlign: "center", height: 150, flexDirection: "column", justifyContent: "center" }}>
                        <Typography sx={{
                            fontSize: {
                                xs: 14, sm: 16, md: 18
                            }, color: colors.grey[700], mt: 2
                        }}>
                            تا کنون هیچ دیدگاهی برای این ویدیو ثبت نشده است.
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: 15, sm: 17, md: 19 }, color: colors.grey[800], fontWeight: 500, mt: 2
                        }}>
                            شما اولین نفر باشید
                        </Typography>
                    </FlexBox>
                )}
            </Box>
        </NoSsr>
    )
}

export const VideoCommentsLoadingSkeleton = memo(() => (
    <Box sx={{ p: 2, bgcolor: "#fff" }}>
        <FlexBox sx={{ mb: 2, height: 48, justifyContent: "space-between" }}>
            <Skeleton height={48} width={114} variant="text" />
            <Skeleton height={48} width={114} variant="rounded" />
        </FlexBox>
        <Skeleton variant="rounded" width="100%" height={150} />
    </Box>
))

const LoadingSkeleton = memo(() => (
    <Box>
        <Skeleton variant="rounded" width="100%" height={150} />
    </Box>
))
